import { z } from 'zod';

export const ApiErrorCodeSchema = z.enum([
  'validation_failed',
  'unauthorized',
  'payment_required',
  'forbidden',
  'not_found',
  'conflict',
  'locked',
  'rate_limited',
  'internal',
  'not_configured',
  'unavailable',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;

export const ApiErrorShapeSchema = z.object({
  error: z.object({
    code: ApiErrorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiErrorShape = z.infer<typeof ApiErrorShapeSchema>;

/**
 * Calendar date (no time): every date-only API field uses this. zod 4
 * deprecates `z.string().date()`, and the repo convention is the explicit
 * regex (see training.ts).
 */
export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date in YYYY-MM-DD format');
export type DateOnly = z.infer<typeof DateOnlySchema>;

export const ListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export function paginatedSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  });
}

export const OkSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof OkSchema>;

/** Plan entitlements enforced server-side. */
export const PLAN_LIMITS = {
  free: { maxMembers: 1 },
  paid: { maxMembers: 100 },
} as const;

/** Starter corpus items created for every new organisation (PLAN §6). */
export const STARTER_CORPUS_ITEMS: ReadonlyArray<{ key: string; title: string }> = [
  // Organisation & governance
  { key: 'company-overview', title: 'Company overview' },
  { key: 'certification-scope', title: 'Scope of certification' },
  { key: 'organisation-chart', title: 'Organisation chart' },
  { key: 'certification-register', title: 'Certification register' },
  { key: 'legal-register', title: 'Legal and regulatory register' },
  // Registers & logs
  { key: 'asset-register', title: 'Asset register' },
  { key: 'supplier-register', title: 'Supplier register' },
  { key: 'risk-register', title: 'Risk register' },
  { key: 'incident-register', title: 'Incident register' },
  { key: 'corrective-action-log', title: 'Corrective action log' },
  { key: 'statement-of-applicability', title: 'Statement of applicability' },
  // Policies
  { key: 'information-security-policy', title: 'Information security policy' },
  { key: 'quality-policy', title: 'Quality policy' },
  { key: 'business-continuity-plan', title: 'Business continuity plan' },
  { key: 'access-control-policy', title: 'Access control policy' },
  { key: 'acceptable-use-policy', title: 'Acceptable use policy' },
  { key: 'information-classification-policy', title: 'Information classification policy' },
  { key: 'network-security-policy', title: 'Network security policy' },
  { key: 'logging-monitoring-policy', title: 'Logging and monitoring policy' },
  { key: 'physical-security-policy', title: 'Physical security policy' },
  { key: 'personnel-security-policy', title: 'Personnel security policy' },
  { key: 'secure-development-policy', title: 'Secure development policy' },
  // Processes & baselines
  { key: 'incident-response-process', title: 'Incident response process' },
  { key: 'backup-process', title: 'Backup process' },
  { key: 'patch-management-process', title: 'Patch management process' },
  { key: 'risk-management-process', title: 'Risk management process' },
  { key: 'change-management-process', title: 'Change management process' },
  { key: 'document-control-process', title: 'Document control process' },
  { key: 'joiners-movers-leavers-process', title: 'Joiners, movers and leavers process' },
  { key: 'secure-configuration-baseline', title: 'Secure configuration baseline' },
  { key: 'malware-protection-process', title: 'Malware protection process' },
  { key: 'secure-disposal-process', title: 'Secure disposal process' },
  // Technical artefacts
  { key: 'network-diagram', title: 'Network diagram' },
  { key: 'data-flow-map', title: 'Data flow map' },
  { key: 'penetration-test-records', title: 'Penetration test records' },
  // Records
  { key: 'training-records', title: 'Training records' },
  { key: 'internal-audit-records', title: 'Internal audit records' },
  { key: 'management-review-records', title: 'Management review records' },
  // Data protection
  { key: 'data-protection-policy', title: 'Data protection policy' },
  { key: 'privacy-notice', title: 'Privacy notice' },
  { key: 'records-of-processing', title: 'Records of processing activities' },
  { key: 'data-subject-rights-procedure', title: 'Data subject rights procedure' },
  { key: 'data-retention-policy', title: 'Data retention policy' },
  { key: 'dpia-register', title: 'DPIA register' },
  { key: 'data-breach-register', title: 'Data breach register' },
  // Clinical safety (DCB 0129 / NHS DTAC)
  { key: 'clinical-risk-management-process', title: 'Clinical risk management process' },
  { key: 'clinical-risk-management-plan', title: 'Clinical risk management plan' },
  { key: 'clinical-safety-case', title: 'Clinical safety case report' },
  { key: 'hazard-log', title: 'Hazard log' },
  // Accessibility
  { key: 'accessibility-statement', title: 'Accessibility statement' },
];
